import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, ModalVariant } from '@patternfly/react-core';
import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';
import { applyFilter } from '@app/utils/parseSearchString';

/** POST/PATCH/PUT/DELETE wrapper that surfaces the `error` field from the JSON body on non-OK responses. */
async function fetchAuditApi<T = any>(
  url: string,
  getToken: () => Promise<string>,
  options?: RequestInit,
): Promise<T> {
  const token = await getToken();
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...options?.headers,
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      if (body?.error) message = body.error;
    } catch { /* ignore parse errors */ }
    throw new Error(message);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

interface EventMapping {
  id: number;
  httpMethod: string;
  pathPattern: string;
  patternType: 'regex' | 'prefix' | 'exact';
  eventName: string;
  enabled: boolean;
  createdBy: string | null;
  createdAt: string;
}

interface AnalyticsModule {
  id: string;
  module_name: string;
  color: string;
  is_active: boolean;
  events: string[];
}

const BLANK_MODULE: Omit<AnalyticsModule, 'id' | 'is_active'> = { module_name: '', color: '#0066cc', events: [] };

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
const PATTERN_TYPES = ['exact', 'prefix', 'regex'] as const;

const EVENT_NAME_HINT = 'UPPERCASE_WITH_UNDERSCORES e.g. MY_FEATURE_LOADED';

export const Analytics: React.FC = () => {
  const getToken = useAccessToken();

  const [mappings, setMappings] = useState<EventMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Form state
  const [method, setMethod] = useState<string>('GET');
  const [pathPattern, setPathPattern] = useState('');
  const [patternType, setPatternType] = useState<'exact' | 'prefix' | 'regex'>('exact');
  const [eventName, setEventName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState(false);

  const [search, setSearch] = useState('');

  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<EventMapping | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // ── Module config state ──────────────────────────────────────────────────
  const [modules, setModules] = useState<AnalyticsModule[]>([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [modulesError, setModulesError] = useState<string | null>(null);

  // Module form (shared for Add and Edit)
  const [editingModule, setEditingModule] = useState<AnalyticsModule | null>(null); // null = Add mode
  const [moduleFormOpen, setModuleFormOpen] = useState(false);
  const [modName, setModName] = useState('');
  const [modColor, setModColor] = useState('#0066cc');
  const [modEvents, setModEvents] = useState(''); // newline-separated event names
  const [modSubmitting, setModSubmitting] = useState(false);
  const [modFormError, setModFormError] = useState<string | null>(null);
  const [pendingDeleteModule, setPendingDeleteModule] = useState<AnalyticsModule | null>(null);
  const [deletingModuleId, setDeletingModuleId] = useState<string | null>(null);

  const filteredMappings = useMemo(
    () => (search.trim() ? applyFilter(mappings, search) : mappings),
    [mappings, search],
  );

  type SortKey = keyof Pick<EventMapping, 'httpMethod' | 'patternType' | 'pathPattern' | 'eventName' | 'createdBy' | 'createdAt' | 'enabled'>;
  const [sortKey, setSortKey] = useState<SortKey>('httpMethod');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortedMappings = useMemo(() => {
    return [...filteredMappings].sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      const cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredMappings, sortKey, sortDir]);

  const loadMappings = useCallback(async (signal?: AbortSignal) => {
    setFetchError(null);
    try {
      const data = await fetchApi<EventMapping[]>('/api/audit/event-mappings', getToken);
      if (!signal?.aborted) setMappings(data);
    } catch (err: any) {
      if (!signal?.aborted) setFetchError(err?.message ?? 'Failed to load mappings');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    const controller = new AbortController();
    loadMappings(controller.signal);
    return () => controller.abort();
  }, [loadMappings]);

  // ── Module config handlers ───────────────────────────────────────────────
  const loadModules = useCallback(async (signal?: AbortSignal) => {
    setModulesError(null);
    try {
      const resp = await fetchApi<{ data: AnalyticsModule[] }>('/api/usage-analytics/module-config', getToken);
      if (!signal?.aborted) setModules(resp.data);
    } catch (err: any) {
      if (!signal?.aborted) setModulesError(err?.message ?? 'Failed to load modules');
    } finally {
      if (!signal?.aborted) setModulesLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    const controller = new AbortController();
    loadModules(controller.signal);
    return () => controller.abort();
  }, [loadModules]);

  const openAddModule = () => {
    setEditingModule(null);
    setModName('');
    setModColor('#0066cc');
    setModEvents('');
    setModFormError(null);
    setModuleFormOpen(true);
  };

  const openEditModule = (m: AnalyticsModule) => {
    setEditingModule(m);
    setModName(m.module_name);
    setModColor(m.color);
    setModEvents(m.events.join('\n'));
    setModFormError(null);
    setModuleFormOpen(true);
  };

  const handleModuleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setModFormError(null);
    const events = modEvents.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    if (!modName.trim()) { setModFormError('Module name is required.'); return; }
    setModSubmitting(true);
    try {
      const body = JSON.stringify({ module_name: modName.trim(), color: modColor, events });
      if (editingModule) {
        await fetchAuditApi(`/api/usage-analytics/module-config/${editingModule.id}`, getToken, { method: 'PUT', body });
      } else {
        await fetchAuditApi('/api/usage-analytics/module-config', getToken, { method: 'POST', body });
      }
      setModuleFormOpen(false);
      await loadModules();
    } catch (err: any) {
      setModFormError(err?.message ?? 'Failed to save module');
    } finally {
      setModSubmitting(false);
    }
  }, [getToken, modName, modColor, modEvents, editingModule, loadModules]);

  const handleToggleModule = useCallback(async (m: AnalyticsModule) => {
    try {
      await fetchAuditApi(`/api/usage-analytics/module-config/${m.id}/toggle`, getToken, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !m.is_active }),
      });
      setModules((prev) => prev.map((x) => x.id === m.id ? { ...x, is_active: !m.is_active } : x));
    } catch (err: any) {
      setModulesError(err?.message ?? 'Failed to toggle module');
    }
  }, [getToken]);

  const handleDeleteModuleConfirmed = useCallback(async () => {
    if (!pendingDeleteModule) return;
    const id = pendingDeleteModule.id;
    setPendingDeleteModule(null);
    setDeletingModuleId(id);
    try {
      await fetchAuditApi(`/api/usage-analytics/module-config/${id}`, getToken, { method: 'DELETE' });
      setModules((prev) => prev.filter((m) => m.id !== id));
    } catch (err: any) {
      setModulesError(err?.message ?? 'Failed to delete module');
    } finally {
      setDeletingModuleId(null);
    }
  }, [getToken, pendingDeleteModule]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setFormError(null);
      setFormSuccess(false);

      if (!pathPattern.trim() || !eventName.trim()) {
        setFormError('All fields are required.');
        return;
      }

      setSubmitting(true);
      try {
        await fetchAuditApi('/api/audit/event-mappings', getToken, {
          method: 'POST',
          body: JSON.stringify({
            httpMethod: method,
            pathPattern: pathPattern.trim(),
            patternType,
            eventName: eventName.trim(),
          }),
        });
        setPathPattern('');
        setEventName('');
        setFormSuccess(true);
        setTimeout(() => setFormSuccess(false), 3000);
        await loadMappings();
      } catch (err: any) {
        setFormError(err?.message ?? 'Failed to register mapping');
      } finally {
        setSubmitting(false);
      }
    },
    [getToken, method, pathPattern, patternType, eventName, loadMappings],
  );

  const handleToggleEnabled = useCallback(
    async (m: EventMapping) => {
      setTogglingId(m.id);
      try {
        const updated = await fetchAuditApi<EventMapping>(`/api/audit/event-mappings/${m.id}`, getToken, {
          method: 'PATCH',
          body: JSON.stringify({ enabled: !m.enabled }),
        });
        setMappings((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      } catch (err: any) {
        setFetchError(err?.message ?? 'Failed to update mapping');
      } finally {
        setTogglingId(null);
      }
    },
    [getToken],
  );

  const handleDeleteConfirmed = useCallback(
    async () => {
      if (!pendingDelete) return;
      const id = pendingDelete.id;
      setPendingDelete(null);
      setDeletingId(id);
      try {
        await fetchAuditApi(`/api/audit/event-mappings/${id}`, getToken, { method: 'DELETE' });
        setMappings((prev) => prev.filter((m) => m.id !== id));
      } catch (err: any) {
        setFetchError(err?.message ?? 'Failed to delete mapping');
      } finally {
        setDeletingId(null);
      }
    },
    [getToken, pendingDelete],
  );

  return (
    <div className="analytics-page">
      <div className="page-header">
        <h1>Analytics — Event Tracking</h1>
        <p>
          Manage audit event mappings. All API routes listed here are automatically recorded under
          their event name. Disable an event to stop tracking it without deleting it.
        </p>
      </div>

      {/* ── Register form ── */}
      <div className="analytics-card">
        <h2 className="analytics-card-title">Register a new event mapping</h2>
        <form className="analytics-form" onSubmit={handleSubmit}>
          <div className="analytics-form-row">
            <div className="analytics-field">
              <label htmlFor="http-method">HTTP Method</label>
              <select
                id="http-method"
                aria-label="HTTP method"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                disabled={submitting}
                className="analytics-select"
              >
                {HTTP_METHODS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            <div className="analytics-field">
              <label htmlFor="pattern-type">Pattern Type</label>
              <select
                id="pattern-type"
                aria-label="Pattern type"
                value={patternType}
                onChange={(e) => setPatternType(e.target.value as 'exact' | 'prefix' | 'regex')}
                disabled={submitting}
                className="analytics-select"
              >
                {PATTERN_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="analytics-field analytics-field--grow">
              <label htmlFor="path-pattern">
                API Path Pattern
                {patternType === 'regex' && (
                  <span className="analytics-hint"> — regex, e.g. \/api\/myfeature\/\d+</span>
                )}
                {patternType === 'prefix' && (
                  <span className="analytics-hint"> — matches any path starting with this</span>
                )}
              </label>
              <input
                id="path-pattern"
                type="text"
                aria-label="API path pattern"
                placeholder={patternType === 'regex' ? '\\/api\\/myfeature\\/\\d+' : '/api/myfeature/data'}
                value={pathPattern}
                onChange={(e) => setPathPattern(e.target.value)}
                disabled={submitting}
                className="analytics-input"
                autoComplete="off"
                spellCheck={false}
              />
            </div>

            <div className="analytics-field analytics-field--grow">
              <label htmlFor="event-name">Event Name</label>
              <input
                id="event-name"
                type="text"
                aria-label="Event name"
                placeholder={EVENT_NAME_HINT}
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                disabled={submitting}
                className="analytics-input"
                autoComplete="off"
                spellCheck={false}
              />
            </div>

            <div className="analytics-field analytics-field--submit">
              <label>&nbsp;</label>
              <button
                type="submit"
                disabled={submitting}
                className="analytics-btn analytics-btn--primary"
              >
                {submitting ? 'Saving…' : 'Add mapping'}
              </button>
            </div>
          </div>

          {formError && <p className="analytics-message analytics-message--error">{formError}</p>}
          {formSuccess && <p className="analytics-message analytics-message--success">Mapping registered successfully.</p>}
        </form>
      </div>

      {/* ── All event mappings table ── */}
      <div className="analytics-card">
        <h2 className="analytics-card-title">Event mappings</h2>

        {loading && <p className="analytics-empty">Loading…</p>}
        {fetchError && <p className="analytics-message analytics-message--error">{fetchError}</p>}

        {!loading && !fetchError && mappings.length === 0 && (
          <p className="analytics-empty">No event mappings found.</p>
        )}

        {!loading && mappings.length > 0 && (
          <div className="analytics-search-bar">
            <input
              className="analytics-search-input"
              type="search"
              placeholder="Filter — plain text or field:value e.g. httpMethod:POST patternType:regex eventName:DEPLOY createdBy:user"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search event mappings"
            />
            <span className="analytics-search-count">
              {filteredMappings.length === mappings.length
                ? `${mappings.length} event${mappings.length !== 1 ? 's' : ''}`
                : `${filteredMappings.length} of ${mappings.length}`}
            </span>
            {search && (
              <button className="analytics-btn analytics-btn--secondary" onClick={() => setSearch('')}>
                Clear
              </button>
            )}
          </div>
        )}

        {!loading && mappings.length > 0 && (
          <div className="analytics-table-wrapper">
            <table className="analytics-table" role="table" aria-label="Event mappings">
              <thead>
                <tr>
                  {([
                    ['httpMethod', 'Method'],
                    ['patternType', 'Type'],
                    ['pathPattern', 'Path Pattern'],
                    ['eventName', 'Event Name'],
                    ['createdBy', 'Added by'],
                    ['createdAt', 'Date'],
                    ['enabled', 'Status'],
                  ] as [SortKey, string][]).map(([key, label]) => (
                    <th
                      key={key}
                      scope="col"
                      className="analytics-th--sortable"
                      onClick={() => handleSort(key)}
                      aria-sort={sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      {label}
                      <span className="analytics-sort-icon" aria-hidden>
                        {sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}
                      </span>
                    </th>
                  ))}
                  <th scope="col"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {sortedMappings.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '24px' }}>
                      No mappings match your search.
                    </td>
                  </tr>
                )}
                {sortedMappings.map((m) => (
                  <tr key={m.id} className={m.enabled ? '' : 'analytics-row--disabled'}>
                    <td><span className={`analytics-method analytics-method--${m.httpMethod.toLowerCase()}`}>{m.httpMethod}</span></td>
                    <td><span className={`analytics-badge analytics-badge--${m.patternType}`}>{m.patternType}</span></td>
                    <td><code className="analytics-code">{m.pathPattern}</code></td>
                    <td><code className="analytics-code">{m.eventName}</code></td>
                    <td className="analytics-cell--secondary">{m.createdBy ?? '—'}</td>
                    <td className="analytics-cell--secondary">{new Date(m.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className={`analytics-btn ${m.enabled ? 'analytics-btn--warning' : 'analytics-btn--secondary'}`}
                        onClick={() => handleToggleEnabled(m)}
                        disabled={togglingId === m.id}
                        aria-label={`${m.enabled ? 'Disable' : 'Enable'} mapping for ${m.httpMethod} ${m.pathPattern}`}
                      >
                        {togglingId === m.id ? '…' : m.enabled ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                    <td>
                      <button
                        className="analytics-btn analytics-btn--danger"
                        onClick={() => setPendingDelete(m)}
                        disabled={deletingId === m.id}
                        aria-label={`Delete mapping for ${m.httpMethod} ${m.pathPattern}`}
                      >
                        {deletingId === m.id ? '…' : 'Remove'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Modal
        variant={ModalVariant.small}
        title="Delete event mapping?"
        isOpen={!!pendingDelete}
        onClose={() => !deletingId && setPendingDelete(null)}
        actions={[
          <Button
            key="confirm"
            variant="danger"
            onClick={handleDeleteConfirmed}
            isLoading={!!deletingId}
            isDisabled={!!deletingId}
          >
            Delete mapping
          </Button>,
          <Button
            key="cancel"
            variant="link"
            onClick={() => setPendingDelete(null)}
            isDisabled={!!deletingId}
          >
            Cancel
          </Button>,
        ]}
      >
        {pendingDelete && (
          <>
            <p>
              You are about to delete the <strong>{pendingDelete.patternType}</strong> mapping for{' '}
              <strong>{pendingDelete.httpMethod} {pendingDelete.pathPattern}</strong>{' '}
              → <strong>{pendingDelete.eventName}</strong>.
            </p>
            <br />
            <p>
              <strong>Already-recorded audit events are not affected.</strong> Historical entries in
              the audit log will remain intact — only the route mapping itself is removed. Future
              requests to this route will no longer be tracked.
            </p>
          </>
        )}
      </Modal>

      {/* ── Analytics Modules ── */}
      <div className="analytics-card">
        <div className="analytics-card-header">
          <h2 className="analytics-card-title">Analytics Modules</h2>
          <p className="analytics-card-desc">
            Group event names into labelled modules for the &ldquo;Calls by Module&rdquo; chart.
            Changes take effect within 60 seconds (server-side cache TTL).
          </p>
          <button className="analytics-btn analytics-btn--primary" onClick={openAddModule}>
            + Add module
          </button>
        </div>

        {modulesLoading && <p className="analytics-empty">Loading…</p>}
        {modulesError && <p className="analytics-message analytics-message--error">{modulesError}</p>}

        {!modulesLoading && modules.length === 0 && !modulesError && (
          <p className="analytics-empty">No modules configured.</p>
        )}

        {!modulesLoading && modules.length > 0 && (
          <div className="analytics-table-wrapper">
            <table className="analytics-table" role="table" aria-label="Analytics modules">
              <thead>
                <tr>
                  <th scope="col">Module</th>
                  <th scope="col">Color</th>
                  <th scope="col">Events</th>
                  <th scope="col">Status</th>
                  <th scope="col"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {modules.map((m) => (
                  <tr key={m.id} className={m.is_active ? '' : 'analytics-row--disabled'}>
                    <td><strong>{m.module_name}</strong></td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        width: 16,
                        height: 16,
                        borderRadius: 3,
                        background: m.color,
                        verticalAlign: 'middle',
                        marginRight: 6,
                        border: '1px solid rgba(0,0,0,.15)',
                      }} />
                      <code className="analytics-code">{m.color}</code>
                    </td>
                    <td>
                      <span className="analytics-cell--secondary" style={{ fontSize: 12 }}>
                        {m.events.length === 0
                          ? <em>No events</em>
                          : m.events.slice(0, 3).map((ev) => (
                              <code key={ev} className="analytics-code" style={{ marginRight: 4 }}>{ev}</code>
                            ))
                        }
                        {m.events.length > 3 && (
                          <span className="analytics-cell--secondary"> +{m.events.length - 3} more</span>
                        )}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`analytics-btn ${m.is_active ? 'analytics-btn--warning' : 'analytics-btn--secondary'}`}
                        onClick={() => handleToggleModule(m)}
                        aria-label={`${m.is_active ? 'Deactivate' : 'Activate'} module ${m.module_name}`}
                      >
                        {m.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        className="analytics-btn analytics-btn--secondary"
                        style={{ marginRight: 6 }}
                        onClick={() => openEditModule(m)}
                      >
                        Edit
                      </button>
                      <button
                        className="analytics-btn analytics-btn--danger"
                        onClick={() => setPendingDeleteModule(m)}
                        disabled={deletingModuleId === m.id}
                        aria-label={`Delete module ${m.module_name}`}
                      >
                        {deletingModuleId === m.id ? '…' : 'Remove'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Module add/edit modal */}
      <Modal
        variant={ModalVariant.medium}
        title={editingModule ? `Edit module: ${editingModule.module_name}` : 'Add analytics module'}
        isOpen={moduleFormOpen}
        onClose={() => !modSubmitting && setModuleFormOpen(false)}
        actions={[
          <Button
            key="save"
            variant="primary"
            onClick={handleModuleSubmit as any}
            isLoading={modSubmitting}
            isDisabled={modSubmitting}
          >
            {editingModule ? 'Save changes' : 'Create module'}
          </Button>,
          <Button key="cancel" variant="link" onClick={() => setModuleFormOpen(false)} isDisabled={modSubmitting}>
            Cancel
          </Button>,
        ]}
      >
        <form onSubmit={handleModuleSubmit} className="analytics-form">
          <div className="analytics-form-row" style={{ flexWrap: 'wrap', gap: 16 }}>
            <div className="analytics-field analytics-field--grow">
              <label htmlFor="mod-name">Module name</label>
              <input
                id="mod-name"
                type="text"
                className="analytics-input"
                placeholder="e.g. Manage Environment"
                value={modName}
                onChange={(e) => setModName(e.target.value)}
                disabled={modSubmitting}
                autoComplete="off"
              />
            </div>
            <div className="analytics-field">
              <label htmlFor="mod-color">Color</label>
              <input
                id="mod-color"
                type="color"
                value={modColor}
                onChange={(e) => setModColor(e.target.value)}
                disabled={modSubmitting}
                style={{ height: 38, width: 60, padding: 2, cursor: 'pointer' }}
              />
            </div>
          </div>
          <div className="analytics-field" style={{ marginTop: 12 }}>
            <label htmlFor="mod-events">
              Event names <span className="analytics-hint">— one per line or comma-separated, SCREAMING_SNAKE_CASE</span>
            </label>
            <textarea
              id="mod-events"
              className="analytics-input"
              rows={6}
              placeholder={'ENV_DEPLOYMENT_INITIATED\nMICROSERVICE_GENERATE_CLICKED\nCHANGE_REQUEST_APPROVED'}
              value={modEvents}
              onChange={(e) => setModEvents(e.target.value)}
              disabled={modSubmitting}
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          </div>
          {modFormError && <p className="analytics-message analytics-message--error">{modFormError}</p>}
        </form>
      </Modal>

      {/* Module delete confirm */}
      <Modal
        variant={ModalVariant.small}
        title="Delete analytics module?"
        isOpen={!!pendingDeleteModule}
        onClose={() => !deletingModuleId && setPendingDeleteModule(null)}
        actions={[
          <Button
            key="confirm"
            variant="danger"
            onClick={handleDeleteModuleConfirmed}
            isLoading={!!deletingModuleId}
            isDisabled={!!deletingModuleId}
          >
            Delete module
          </Button>,
          <Button key="cancel" variant="link" onClick={() => setPendingDeleteModule(null)} isDisabled={!!deletingModuleId}>
            Cancel
          </Button>,
        ]}
      >
        {pendingDeleteModule && (
          <p>
            Deleting <strong>{pendingDeleteModule.module_name}</strong> will remove it from the
            analytics charts. Historical audit events are not affected — they remain in the database.
          </p>
        )}
      </Modal>
    </div>
  );
};
